import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Upload, X, FileText } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { createKycRequest } from '@/api/kyc';

interface CreateKycRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerId: string;
    customerName: string;
    onSuccess: () => void;
}

interface DocumentFile {
    file: File;
    type: string;
    id: string;
}

const CreateKycRequestModal: React.FC<CreateKycRequestModalProps> = ({
    isOpen,
    onClose,
    customerId,
    customerName,
    onSuccess,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [documents, setDocuments] = useState<DocumentFile[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const documentTypes = [
        { value: 'passport', label: 'Passport' },
        { value: 'drivers_license', label: "Driver's License" },
        { value: 'utility_bill', label: 'Utility Bill' },
    ];

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
        const file = event.target.files?.[0];
        if (file) {
            const newDocument: DocumentFile = {
                file,
                type,
                id: Math.random().toString(36).substr(2, 9),
            };
            setDocuments(prev => [...prev, newDocument]);
        }
    };

    const removeDocument = (id: string) => {
        setDocuments(prev => prev.filter(doc => doc.id !== id));
    };

    const handleSubmit = async () => {
        if (documents.length === 0) {
            enqueueSnackbar('Please upload at least one document', { variant: 'error' });
            return;
        }

        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('customer_id', customerId);

            documents.forEach((doc, index) => {
                formData.append(`documents[${index}][file]`, doc.file);
                formData.append(`documents[${index}][type]`, doc.type);
            });

            await createKycRequest(formData);

            enqueueSnackbar('KYC request created successfully', { variant: 'success' });
            onSuccess();
            onClose();
            setDocuments([]);
        } catch (error) {
            console.error('Error creating KYC request:', error);
            enqueueSnackbar('Failed to create KYC request', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setDocuments([]);
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Create KYC Request</DialogTitle>
                    <DialogDescription>
                        Create a new KYC verification request for {customerName}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Customer Info */}
                    <div className="p-4 bg-muted rounded-lg">
                        <div className="text-sm text-muted-foreground">Customer</div>
                        <div className="font-medium">{customerName}</div>
                        <div className="text-sm text-muted-foreground">ID: {customerId}</div>
                    </div>

                    {/* Document Upload Section */}
                    <div className="space-y-4">
                        <div>
                            <Label className="text-base font-medium">Upload Documents</Label>
                            <p className="text-sm text-muted-foreground">
                                Upload at least one document for KYC verification
                            </p>
                        </div>

                        {/* Document Type Selection */}
                        <div className="grid grid-cols-3 gap-4">
                            {documentTypes.map(type => (
                                <div key={type.value} className="space-y-2">
                                    <Label htmlFor={`file-${type.value}`} className="text-sm">
                                        {type.label}
                                    </Label>
                                    <Input
                                        id={`file-${type.value}`}
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        onChange={e => handleFileChange(e, type.value)}
                                        className="text-sm"
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Uploaded Documents List */}
                        {documents.length > 0 && (
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Uploaded Documents</Label>
                                <div className="space-y-2">
                                    {documents.map(doc => (
                                        <div
                                            key={doc.id}
                                            className="flex items-center justify-between p-3 bg-muted rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <FileText className="h-4 w-4 text-muted-foreground" />
                                                <div>
                                                    <div className="text-sm font-medium">
                                                        {doc.file.name}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {
                                                            documentTypes.find(
                                                                t => t.value === doc.type
                                                            )?.label
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeDocument(doc.id)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || documents.length === 0}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        {isSubmitting ? 'Creating...' : 'Create KYC Request'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CreateKycRequestModal;
